import i18n from "@/i18n";
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { api } from "@/lib/api";

// CLOCK C3 — temporary support / replacement recorded by the receiving
// restaurant. The employee keeps the main restaurant. Not QR agency work.
const REASONS = {
    get SUPPORT() { return i18n.t("wf.supportAssignmentsPanel.support", "Support"); },
    get REPLACEMENT() { return i18n.t("wf.supportAssignmentsPanel.replacement", "Replacement"); },
};
const today = () => new Date().toISOString().slice(0, 10);

const SupportForm = ({ candidates, restaurantId, onSaved }) => {
    const [f, setF] = useState({ employee_id: "", start_date: today(), end_date: today(), reason: "SUPPORT", note: "" });
    const [busy, setBusy] = useState(false);
    const set = (k) => (v) => setF((x) => ({ ...x, [k]: v }));
    const save = async () => {
        setBusy(true);
        try {
            await api.post("/workforce/support-assignments", { restaurant_id: restaurantId, ...f, note: f.note || null });
            toast.success(i18n.t("wf.supportAssignmentsPanel.supportRecorded", "Support recorded"));
            setF((x) => ({ ...x, employee_id: "", note: "" }));
            onSaved();
        } catch (e) {
            toast.error(e?.response?.data?.detail?.reason || "Could not record support");
        } finally { setBusy(false); }
    };
    return (
        <div className="flex flex-wrap items-end gap-2" data-testid="support-assignment-form">
            <Select value={f.employee_id} onValueChange={set("employee_id")}>
                <SelectTrigger className="w-56 bg-white" data-testid="support-employee-select"><SelectValue placeholder={i18n.t("wf.supportAssignmentsPanel.employee", "Employee")} /></SelectTrigger>
                <SelectContent>
                    {candidates.map((c) => (
                        <SelectItem key={c.employee_id} value={c.employee_id} data-testid={`support-employee-option-${c.employee_id}`}>
                            {c.first_name} {c.last_name}
                        </SelectItem>
                    ))}
                </SelectContent>
            </Select>
            <Input type="date" className="w-40 bg-white" value={f.start_date} onChange={(e) => set("start_date")(e.target.value)} data-testid="support-start-date" />
            <Input type="date" className="w-40 bg-white" value={f.end_date} onChange={(e) => set("end_date")(e.target.value)} data-testid="support-end-date" />
            <Select value={f.reason} onValueChange={set("reason")}>
                <SelectTrigger className="w-40 bg-white" data-testid="support-reason-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                    {Object.entries(REASONS).map(([k, v]) => (
                        <SelectItem key={k} value={k} data-testid={`support-reason-option-${k}`}>{v}</SelectItem>
                    ))}
                </SelectContent>
            </Select>
            <Input className="w-48 bg-white" placeholder={i18n.t("wf.supportAssignmentsPanel.noteOptional", "Note (optional)")} value={f.note} maxLength={500}
                onChange={(e) => set("note")(e.target.value)} data-testid="support-note" />
            <Button size="sm" disabled={busy || !f.employee_id} onClick={save} data-testid="support-assignment-save">{i18n.t("wf.supportAssignmentsPanel.addSupport", "Add support")}</Button>
        </div>
    );
};

export const SupportAssignmentsPanel = ({ restaurantId, onChanged }) => {
    const [data, setData] = useState({ assignments: [], candidates: [] });
    const load = useCallback(async () => {
        if (!restaurantId) return;
        try {
            const { data: d } = await api.get("/workforce/support-assignments", { params: { restaurant_id: restaurantId, as_of: today() } });
            if (d && Array.isArray(d.assignments)) setData(d);
        } catch { /* panel stays empty */ }
    }, [restaurantId]);
    useEffect(() => { load(); }, [load]);
    const changed = () => { load(); if (onChanged) onChanged(); };
    const cancel = async (id) => {
        try {
            await api.post(`/workforce/support-assignments/${id}/cancel`, { restaurant_id: restaurantId });
            changed();
        } catch (e) {
            toast.error(e?.response?.data?.detail?.reason === "SHIFTS_EXIST_IN_ASSIGNMENT"
                ? i18n.t("wf.supportAssignmentsPanel.removeTheSupportShiftsFirst", "Remove the support shifts first") : i18n.t("wf.supportAssignmentsPanel.couldNotCancel", "Could not cancel"));
        }
    };
    const active = data.assignments.filter((a) => a.status === "ACTIVE");
    return (
        <section data-testid="support-assignments-panel" className="rounded-md border border-neutral-200 p-3 space-y-2 text-sm">
            <h3 className="font-medium">{i18n.t("wf.supportAssignmentsPanel.temporarySupportFromOtherRestaurants", "Temporary support from other restaurants")}</h3>
            <SupportForm candidates={data.candidates} restaurantId={restaurantId} onSaved={changed} />
            {active.map((a) => (
                <div key={a.assignment_id} className="flex items-center gap-3" data-testid={`support-assignment-${a.assignment_id}`}>
                    <span>{a.first_name} {a.last_name} · {a.start_date}–{a.end_date} · {REASONS[a.reason]}</span>
                    <Button size="sm" variant="outline" onClick={() => cancel(a.assignment_id)}
                        data-testid={`support-assignment-cancel-${a.assignment_id}`}>{i18n.t("common.cancel", "Cancel")}</Button>
                </div>
            ))}
        </section>
    );
};

export default SupportAssignmentsPanel;
